import moment from 'moment-timezone';
import { PriceOptionData, PromoPriceData } from '../models/concession/price-option';
import { TimeHelper } from './time.helper';

export class PriceOptionHelper {
  static computePromoPriceCents(baseCents: number, promo: PromoPriceData): number {
    const base = Number.isFinite(baseCents) ? baseCents : 0;
    const ruleType = promo.ruleType || 'fixed';
    if (ruleType === 'percentage') {
      const percent = promo.ruleValue ?? 0;
      return Math.max(0, Math.round(base * (1 - percent / 100)));
    }
    if (ruleType === 'amount-off') {
      return Math.max(0, Math.round(base - (promo.ruleValue ?? 0)));
    }
    return Math.max(0, Math.round(promo.price ?? base));
  }

  static scalePromoTax(promo: PromoPriceData, priceCents: number): { preTaxPrice?: number; taxAmount?: number } {
    const snapshot = promo.price || 0;
    if (!snapshot || snapshot === priceCents) {
      return { preTaxPrice: promo.preTaxPrice, taxAmount: promo.taxAmount };
    }
    const ratio = priceCents / snapshot;
    const taxAmount =
      promo.taxAmount != null ? Math.round(promo.taxAmount * ratio) : undefined;
    const preTaxPrice =
      promo.preTaxPrice != null ? Math.round(promo.preTaxPrice * ratio) : undefined;
    return { preTaxPrice, taxAmount };
  }

  static dateBoundExcludes(value: string, edge: 'start' | 'end'): boolean {
    const dateOnly = String(value).match(/^(\d{4}-\d{2}-\d{2})$/);
    const today = moment().format('YYYY-MM-DD');
    if (dateOnly) {
      if (!moment(dateOnly[1], 'YYYY-MM-DD', true).isValid()) {
        return true;
      }
      return edge === 'start' ? today < dateOnly[1] : today > dateOnly[1];
    }
    const instant = new Date(value);
    if (Number.isNaN(instant.getTime())) {
      return true;
    }
    const now = new Date();
    return edge === 'start' ? now < instant : now > instant;
  }

  static getActivePriceOption(priceOption: PriceOptionData, dayOfWeek: string): PriceOptionData {
    if (!priceOption.promoPricing?.length) {
      return priceOption;
    }

    const activePromoPrice = priceOption.promoPricing.find(promoPrice => {
      if (promoPrice.isActive === false) {
        return false;
      }
      const days = promoPrice.daysOfWeek || [];
      if (!days.includes(dayOfWeek)) {
        return false;
      }
      if (promoPrice.startDate && PriceOptionHelper.dateBoundExcludes(promoPrice.startDate, 'start')) {
        return false;
      }
      if (promoPrice.endDate && PriceOptionHelper.dateBoundExcludes(promoPrice.endDate, 'end')) {
        return false;
      }
      return TimeHelper.isWithinTimeFrame(promoPrice.startTimeString, promoPrice.endTimeString);
    });

    if (!activePromoPrice) {
      return priceOption;
    }

    const price = PriceOptionHelper.computePromoPriceCents(priceOption.price || 0, activePromoPrice);
    const tax = PriceOptionHelper.scalePromoTax(activePromoPrice, price);

    return {
      ...priceOption,
      preDiscountPrice: priceOption.price,
      preTaxPrice: tax.preTaxPrice,
      taxAmount: tax.taxAmount ?? priceOption.taxAmount,
      price,
      tag: activePromoPrice.tag,
    };
  }
}
